import React from 'react';
import { CheckCircle2, Play, Terminal } from 'lucide-react';

export const Hero: React.FC = () => {
  return (
    <section className="relative min-h-screen flex flex-col justify-center pt-24 pb-12 overflow-hidden bg-background">
      <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-hero-glow opacity-60 pointer-events-none rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="flex flex-col gap-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white border border-gray-200 w-fit shadow-sm">
            <div className="w-2 h-2 rounded-full bg-js animate-pulse shadow-[0_0_8px_rgba(247,223,30,0.8)]" />
            <span className="font-mono text-xs text-gray-500">
              Runtime: <span className="text-gray-900 font-semibold">Production</span>
            </span>
          </div>

          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-gray-900 leading-[1.1]">
            <span className="text-dotnet">Built</span> to scale.
            <br />
            <span className="text-amber-500">Rapid</span> iteration.
          </h1>

          <p className="text-lg text-gray-600 font-mono leading-relaxed border-l-2 border-dotnet/30 pl-4">
            <span className="text-syntaxKeyword font-bold">public async</span>{' '}
            <span className="text-syntaxType">Task</span>&lt;
            <span className="text-syntaxType">Future</span>&gt;{' '}
            <span className="text-syntaxFunction">BuildAsync</span>()
            <br />
            Our .NET pipelines are battle tested at scale, up to 150 billion records and terabytes of data in SQL Server and Postgres.
          </p>

          <div className="flex flex-wrap gap-4 mt-4">
            <a
              href="#work"
              className="group relative px-6 py-3 bg-dotnet hover:bg-dotnet/90 text-white font-mono text-sm rounded-sm transition-all flex items-center gap-2 shadow-lg shadow-dotnet/20 hover:shadow-dotnet/40 hover:-translate-y-0.5"
            >
              <Play size={16} fill="currentColor" />
              <span>dotnet run</span>
            </a>
            <a
              href="#contact"
              className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-mono text-sm rounded-sm border border-gray-200 shadow-sm transition-all flex items-center gap-2 hover:border-gray-300"
            >
              <Terminal size={16} />
              <span>npm start project</span>
            </a>
          </div>

          <div className="flex items-center gap-6 mt-8 text-xs font-mono text-gray-500">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-ts" />
              <span>Type Safe</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-amber-500" />
              <span>Non-blocking</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-dotnet" />
              <span>Compiled</span>
            </div>
          </div>
        </div>

        <div className="relative hidden lg:block">
          <div className="bg-white rounded-lg border border-gray-200 shadow-2xl overflow-hidden font-mono text-xs xl:text-sm code-shadow flex flex-col max-h-[600px] w-full transform transition-transform hover:scale-[1.01] duration-500">
            <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b border-gray-200 shrink-0">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400 border border-red-500/20" />
                <div className="w-3 h-3 rounded-full bg-yellow-400 border border-yellow-500/20" />
                <div className="w-3 h-3 rounded-full bg-green-400 border border-green-500/20" />
              </div>
              <div className="text-gray-600 text-xs font-medium flex items-center gap-2 overflow-hidden px-4">
                <span className="opacity-50 truncate">src/libraries/System.Console/src/System/</span>
                <span className="whitespace-nowrap">Console.cs</span>
              </div>
              <div className="w-10" />
            </div>

            <div className="p-6 text-gray-800 leading-relaxed overflow-hidden bg-white grow relative">
              <div className="flex">
                <div className="flex flex-col text-right pr-4 text-gray-300 select-none border-r border-gray-100 mr-4 font-mono text-[10px] xl:text-xs leading-6 shrink-0">
                  {Array.from({ length: 24 }).map((_, index) => (
                    <span key={index} className="block h-6">
                      {index + 1}
                    </span>
                  ))}
                </div>

                <div className="whitespace-pre-wrap font-medium text-[10px] xl:text-xs leading-6 min-w-0 break-words">
                  <span className="text-syntaxComment">
                    {'// Licensed to the .NET Foundation under one or more agreements.'}
                  </span>
                  <br />
                  <span className="text-syntaxComment">
                    {'// The .NET Foundation licenses this file to you under the MIT license.'}
                  </span>
                  <br />
                  <br />
                  <span className="text-syntaxKeyword">using</span> System.Diagnostics;
                  <br />
                  <span className="text-syntaxKeyword">using</span> System.IO;
                  <br />
                  <span className="text-syntaxKeyword">using</span> System.Text;
                  <br />
                  <span className="text-syntaxKeyword">using</span> System.Threading;
                  <br />
                  <br />
                  <span className="text-syntaxKeyword">namespace</span>{' '}
                  <span className="text-syntaxType">System</span>
                  <br />
                  {'{'}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="text-syntaxKeyword">public static class</span>{' '}
                  <span className="text-syntaxType">Console</span>
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;{'{'}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxComment">{'// Unlike many other buffer sizes...'}</span>
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">internal const int</span> ReadBufferSize = 4096;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private const int</span> WriteBufferSize = 256;
                  <br />
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private static readonly object</span> s_syncObject ={' '}
                  <span className="text-syntaxKeyword">new</span>{' '}
                  <span className="text-syntaxKeyword">object</span>();
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private static</span>{' '}
                  <span className="text-syntaxType">TextReader</span>? s_in;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private static</span>{' '}
                  <span className="text-syntaxType">TextWriter</span>? s_out, s_error;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private static</span>{' '}
                  <span className="text-syntaxType">Encoding</span>? s_inputEncoding;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private static</span>{' '}
                  <span className="text-syntaxType">Encoding</span>? s_outputEncoding;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-syntaxKeyword">private static bool</span> s_isOutTextWriterRedirected;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;{'}'}
                  <br />
                  {'}'}
                </div>
              </div>
            </div>

            <div className="bg-[#007acc] text-white px-3 py-1 text-xs flex justify-between items-center shrink-0">
              <div className="flex gap-4">
                <span>master*</span>
                <span>0 errors</span>
                <span>0 warnings</span>
              </div>
              <div className="flex gap-4">
                <span>Ln 24, Col 1</span>
                <span>UTF-8</span>
                <span>C#</span>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-10 -left-10 w-2/3 bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden font-mono text-sm transform rotate-3 hover:rotate-0 transition-transform duration-500 hover:z-20">
            <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-100">
              <span className="text-xs text-gray-500">script.js</span>
            </div>
            <div className="p-4 text-gray-800 leading-relaxed">
              <span className="text-syntaxVariable">console</span>.<span className="text-syntaxFunction">log</span>(
              <br />
              &nbsp;&nbsp;<span className="text-syntaxString">"Multiple styles: %cred %corange"</span>,
              <br />
              &nbsp;&nbsp;<span className="text-syntaxString">"color: red"</span>,
              <br />
              &nbsp;&nbsp;<span className="text-syntaxString">"color: orange"</span>,
              <br />
              &nbsp;&nbsp;<span className="text-syntaxString">"Additional unformatted message"</span>,
              <br />
              );
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
